import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  body_markdown: string;
  cover_image: string | null;
  author_id: string | null;
  chapter: "washington" | "union" | "all";
  category: string;
  status: "draft" | "published";
  published_at: string | null;
  created_at: string;
  updated_at: string;
  author?: { id: string; name: string; image: string | null } | null;
}

const POST_SELECT = `
  *,
  author:members!posts_author_id_fkey(id, name, image)
`;

export const usePublishedPosts = () =>
  useQuery({
    queryKey: ["posts", "published"],
    queryFn: async (): Promise<Post[]> => {
      const { data, error } = await supabase
        .from("posts")
        .select(POST_SELECT)
        .eq("status", "published")
        .order("published_at", { ascending: false });
      if (error) throw error;
      return (data as any) ?? [];
    },
  });

export const usePostBySlug = (slug: string | undefined) =>
  useQuery({
    enabled: !!slug,
    queryKey: ["posts", "slug", slug],
    queryFn: async (): Promise<Post | null> => {
      const { data, error } = await supabase
        .from("posts")
        .select(POST_SELECT)
        .eq("slug", slug!)
        .maybeSingle();
      if (error) throw error;
      return (data as any) ?? null;
    },
  });

export const useAllPostsForAdmin = (refreshKey: number = 0) =>
  useQuery({
    queryKey: ["posts", "admin", refreshKey],
    queryFn: async (): Promise<Post[]> => {
      const { data, error } = await supabase
        .from("posts")
        .select(POST_SELECT)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data as any) ?? [];
    },
  });

export const slugify = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .substring(0, 80);
